import { eq, and } from 'drizzle-orm'
import { inngest } from '@/lib/inngest'
import { db as defaultDb } from '@/db'
import { tickets, ticketMessages, aiArtifacts, tenantConfigs, auditLog } from '@/db/schema'
import { buildSentimentPrompt } from '../../prompts/sentiment-assessment'
import { callSentimentLLM, type SentimentResult, type AIGatewayConfig } from '@/lib/ai-gateway'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

type DB = PostgresJsDatabase<typeof schema>

export type AssessSentimentInput = {
  ticketId: string
  tenantId: string
  messageId: string
  ticketVersion: number
}

export async function runSentimentAssessment(
  input: AssessSentimentInput,
  options?: {
    db?: DB
    callLLM?: (config: AIGatewayConfig, system: string, user: string) => Promise<SentimentResult>
  },
): Promise<{ outcome: 'assessed' | 'stale' | 'degraded'; result?: SentimentResult }> {
  const db = (options?.db ?? defaultDb) as DB
  const llm = options?.callLLM ?? callSentimentLLM

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, input.ticketId), eq(tickets.tenantId, input.tenantId)))
    .limit(1)

  if (!ticket) throw new Error(`Ticket not found: ${input.ticketId}`)

  // Stale-run guard: if the artifact for this trigger already exists, skip duplicate work
  const [existing] = await db
    .select({ id: aiArtifacts.id })
    .from(aiArtifacts)
    .where(and(
      eq(aiArtifacts.ticketId, input.ticketId),
      eq(aiArtifacts.artifactType, 'sentiment_analysis'),
      eq(aiArtifacts.sourceMessageId, input.messageId),
    ))
    .limit(1)

  if (existing) return { outcome: 'stale' }

  const [config] = await db
    .select()
    .from(tenantConfigs)
    .where(eq(tenantConfigs.tenantId, input.tenantId))
    .limit(1)

  if (!config) throw new Error(`Tenant config not found: ${input.tenantId}`)

  const messages = await db
    .select({ role: ticketMessages.role, body: ticketMessages.bodySanitized })
    .from(ticketMessages)
    .where(eq(ticketMessages.ticketId, input.ticketId))
    .orderBy(ticketMessages.createdAt)

  const { system, user } = buildSentimentPrompt({
    referenceNumber: ticket.referenceNumber,
    submissionText: messages[0]?.body ?? '',
    issueCategory: ticket.issueCategory ?? undefined,
    affectedSystem: ticket.affectedSystem ?? undefined,
    userPriority: ticket.userPriority ?? undefined,
    messages,
  })

  let result: SentimentResult
  try {
    result = await llm(
      {
        provider: config.aiProvider,
        model: config.aiModel,
        temperature: config.aiTemperature,
        dataProcessingConsent: config.dataProcessingConsent,
      },
      system,
      user,
    )
  } catch (err) {
    await db.insert(auditLog).values({
      tenantId: input.tenantId,
      actorId: null,
      actorType: 'ai_agent',
      action: 'ticket.ai_degraded',
      entityType: 'ticket',
      entityId: input.ticketId,
      payload: { hook: 'sentiment-assessment', error: err instanceof Error ? err.message : 'Unknown error' },
    })
    return { outcome: 'degraded' }
  }

  // Write sentiment_analysis artifact
  await db.insert(aiArtifacts).values({
    tenantId: input.tenantId,
    ticketId: input.ticketId,
    sourceMessageId: input.messageId,
    triggerEvent: 'ticket/triaged',
    artifactType: 'sentiment_analysis',
    content: `Urgency: ${result.urgency_score.toFixed(2)} · Sentiment: ${result.sentiment_label} · Priority: ${result.priority_band}. ${result.reasoning}`,
    contentJson: JSON.stringify(result),
    modelUsed: config.aiModel,
    promptVersion: '1.0.0',
    validationStatus: 'valid',
  })

  // Update ticket enrichment fields — no version guard needed (enrichment only, no lifecycle change)
  await db
    .update(tickets)
    .set({
      urgencyScore: result.urgency_score,
      sentimentScore: result.sentiment_score,
      priority: result.priority_band,
      updatedAt: new Date(),
    })
    .where(eq(tickets.id, input.ticketId))

  await db.insert(auditLog).values({
    tenantId: input.tenantId,
    actorId: null,
    actorType: 'ai_agent',
    action: 'ticket.sentiment_assessed',
    entityType: 'ticket',
    entityId: input.ticketId,
    payload: {
      urgency_score: result.urgency_score,
      sentiment_label: result.sentiment_label,
      priority_band: result.priority_band,
    },
  })

  return { outcome: 'assessed', result }
}

export const sentimentAssessmentFn = inngest.createFunction(
  { id: 'sentiment-urgency-assessment', retries: 3, triggers: [{ event: 'ticket/triaged' }] },
  async ({ event, step }) => {
    return await step.run('run-sentiment-assessment', () =>
      runSentimentAssessment(event.data as AssessSentimentInput),
    )
  },
)
